/**
 * "Review application": the application exactly as sent (coverage, answers and the consents given),
 * read-only. Loads the stored record only when opened.
 */
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Skeleton,
} from '@design-system';
import { useState } from 'react';
import { useConsumerApplicationRecords } from '../hooks/use-consumer-application-records';
import { ApplicationAnswersSummary } from './application-answers-summary';

export function ApplicationReviewDialog({
  clientId,
  applicationId,
}: {
  clientId: string;
  applicationId: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const records = useConsumerApplicationRecords(clientId, { enabled: isOpen });
  const record = records.data?.find(candidate => candidate.id === applicationId);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="self-start"
        >
          Review application
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Your submitted application</DialogTitle>
          <DialogDescription>This is what you sent. It can’t be changed.</DialogDescription>
        </DialogHeader>
        {records.isLoading && <Skeleton className="h-40 rounded-lg" />}
        {records.isError && (
          <Alert variant="destructive">
            <AlertDescription className="flex flex-col items-start gap-2">
              We couldn’t load your application.
              <Button
                variant="outline"
                size="sm"
                onClick={() => void records.refetch()}
              >
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {record && (
          <div className="flex flex-col gap-4">
            <ApplicationAnswersSummary answers={record.answers} />
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-gray-950">Consents you gave</h3>
              <ul
                className="text-muted-foreground flex list-disc flex-col gap-1 pl-5 text-sm"
                data-testid="application-consents-given"
              >
                {record.consents.map(consent => (
                  <li key={consent.consentId}>
                    {consent.title ?? consent.checkbox ?? consent.consentId}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
